import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ApiError } from "@carepass/api-client";
import { useAdminApplication } from "../../app/providers";
import { TotpSetupView } from "./components/TotpSetupView";
import type { LoginFeedback } from "./components/LoginView";
import { useAdminTotpCommands } from "./queries";
import type { Schemas } from "../workspace/api.generated";

const TOTP_FEEDBACK: Readonly<Record<string, LoginFeedback>> = {
  ADMIN_TOTP_INVALID: {
    title: "TOTP 코드를 확인해 주세요",
    message: "인증 앱에 표시된 현재 숫자 6자리를 다시 입력해 주세요.",
  },
  ADMIN_TOTP_ENROLLMENT_NOT_FOUND: {
    title: "등록 정보가 만료되었습니다",
    message: "새 등록 정보를 발급받아 다시 연결해 주세요.",
  },
};

function totpFeedback(error: unknown): LoginFeedback {
  if (error instanceof ApiError) {
    const known = TOTP_FEEDBACK[error.code];
    if (known) return known;
    if (error.kind === "network") {
      return {
        title: "서버에 연결하지 못했습니다",
        message: "네트워크 연결을 확인한 뒤 다시 시도해 주세요.",
      };
    }
  }
  return {
    title: "TOTP 설정을 계속하지 못했습니다",
    message: "등록 정보를 확인한 뒤 다시 시도해 주세요.",
  };
}

export function TotpSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionLifecycle } = useAdminApplication();
  const commands = useAdminTotpCommands();
  const [enrollment, setEnrollment] = useState<Schemas["AdminTotpEnrollmentResponse"] | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[] | null>(null);
  const [operationsSession, setOperationsSession] = useState<Schemas["AuthenticateAdminResponse"] | null>(null);
  const [error, setError] = useState<LoginFeedback | null>(null);
  const [issuing, setIssuing] = useState(false);

  const beginEnrollment = async () => {
    if (issuing || enrollment) return;
    setIssuing(true);
    setError(null);
    try {
      setEnrollment(await commands.beginEnrollment());
    } catch (cause) {
      if (!(cause instanceof ApiError && cause.kind === "aborted")) setError(totpFeedback(cause));
    } finally {
      setIssuing(false);
    }
  };

  const confirmEnrollment = async (
    input: Schemas["ConfirmAdminTotpRequest"],
  ): Promise<boolean> => {
    setError(null);
    try {
      const result = await commands.confirmEnrollment(input);
      if (!result.recoveryCodes.length) {
        sessionLifecycle.end("totp-confirm-uncertain");
        void navigate(`/login${location.search}`, { replace: true });
        return false;
      }
      setRecoveryCodes(result.recoveryCodes);
      setOperationsSession(result.session);
      setEnrollment(null);
      return true;
    } catch (cause) {
      if (cause instanceof ApiError && cause.kind === "aborted") return false;
      if (cause instanceof ApiError && cause.kind === "network") {
        sessionLifecycle.end("totp-confirm-uncertain");
        void navigate(`/login${location.search}`, { replace: true });
        return false;
      }
      if (cause instanceof ApiError && cause.code === "ADMIN_TOTP_ENROLLMENT_NOT_FOUND") {
        setEnrollment(null);
      }
      setError(totpFeedback(cause));
      return false;
    }
  };

  const recoveryCodesStored = () => {
    if (!operationsSession) return;
    sessionLifecycle.start(operationsSession);
    setRecoveryCodes(null);
    setOperationsSession(null);
    void navigate(`/login${location.search}`, { replace: true });
  };

  return (
    <TotpSetupView
      enrollment={enrollment}
      error={error}
      issuing={issuing}
      recoveryCodes={recoveryCodes}
      onBeginEnrollment={beginEnrollment}
      onClearError={() => setError(null)}
      onConfirm={confirmEnrollment}
      onCopyEnrollmentSecret={async () => {
        if (!enrollment?.secret || !navigator.clipboard) throw new Error("CLIPBOARD_UNAVAILABLE");
        await navigator.clipboard.writeText(enrollment.secret);
      }}
      onCopyRecoveryCodes={async () => {
        if (!recoveryCodes?.length || !navigator.clipboard) throw new Error("CLIPBOARD_UNAVAILABLE");
        await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      }}
      onRecoveryCodesStored={recoveryCodesStored}
    />
  );
}
